/**
 * /financeiro/dre — DRE em regime de competência (ADR-0029) · redesign FIN-5.
 *
 * Demonstração de Resultado em CASCATA, fiel ao mockup Fase 5 V2.2 (#dre):
 * seções por classificação → barras proporcionais → subtotais (Receita
 * líquida, Lucro bruto, Resultado operacional) → Resultado líquido em
 * destaque → memorando colapsável (blocos que não compõem o resultado).
 *
 * TODA a aritmética contábil vive no backend (`cascata_dre` de
 * /api/financeiro/dre). O frontend SÓ exibe — nenhum cálculo financeiro aqui.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiAlertTriangle, FiChevronDown } from "react-icons/fi";

import ExportMenu from "../../../components/ExportMenu";
import { useAuth } from "../../../context/AuthContext";
import { formatarReais, obterDre } from "../../../services/financeiro";
import { PageContainer } from "../../../styles/shared";
import { exportarDre } from "../_shared/exportarRelatorios";

const MESES = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];

function isoLocal(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function brData(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

// Presets de período → intervalo [inicio, fim] (período fechado, acumulado).
function intervaloDe(periodo, base = new Date()) {
  const y = base.getFullYear();
  const m = base.getMonth();
  if (periodo === "ano") return [`${y}-01-01`, `${y}-12-31`];
  if (periodo === "trimestre") {
    const im = Math.floor(m / 3) * 3;
    return [isoLocal(new Date(y, im, 1)), isoLocal(new Date(y, im + 3, 0))];
  }
  return [isoLocal(new Date(y, m, 1)), isoLocal(new Date(y, m + 1, 0))];
}
function tituloPeriodo(periodo, base = new Date()) {
  const y = base.getFullYear();
  if (periodo === "ano") return `${y}`;
  if (periodo === "trimestre") return `Q${Math.floor(base.getMonth() / 3) + 1} ${y}`;
  return `${MESES[base.getMonth()]} ${y}`;
}

const PERIODOS = [
  { id: "mes", label: "Mês" },
  { id: "trimestre", label: "Trimestre" },
  { id: "ano", label: "Ano" },
];


/** Uma linha da cascata (seção / item / subtotal / total), 100% data-driven. */
function DreLinha({ l }) {
  const isSecao = l.nivel === "secao";
  const isTotal = l.nivel === "total";
  const isSub = l.nivel === "subtotal";

  const cls = ["dre-row"];
  if (isSecao) cls.push("section");
  if (isSub) cls.push("subtotal");
  if (isTotal) cls.push("total");

  const fillCls = (l.natureza === "despesa" || !l.positivo) ? "negative" : "positive";

  let corValor;
  if (isTotal) corValor = "var(--accent)";
  else if (isSub) corValor = l.positivo ? "var(--success-text)" : "var(--danger-text)";
  else if (l.natureza === "receita") corValor = "var(--success-text)";

  return (
    <div className={cls.join(" ")}>
      <div className="label" style={(isSub || isTotal) ? { fontWeight: 600 } : undefined}>
        {l.label}
        {l.sub ? <span className="sub">{l.sub}</span> : null}
      </div>
      {isSecao ? <div /> : (
        <div className="bar">
          {l.barra_pct != null && (
            <div className={"fill " + fillCls} style={{ width: `${l.barra_pct}%` }} />
          )}
        </div>
      )}
      <div className="val" style={corValor ? { color: corValor } : undefined}>
        {isSecao ? "R$" : `${l.positivo ? "+" : "−"}${formatarReais(l.valor)}`}
        {l.delta ? <span className="delta">{l.delta}</span> : null}
      </div>
    </div>
  );
}


export default function DRE() {
  const { request } = useAuth();

  const [periodo, setPeriodo] = useState("mes");
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [memoAberto, setMemoAberto] = useState(true);

  const hoje = useMemo(() => new Date(), []);
  const [inicio, fim] = useMemo(() => intervaloDe(periodo, hoje), [periodo, hoje]);

  const carregar = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const d = await obterDre(request, { inicio, fim });
      setDados(d);
    } catch (err) {
      setError(err?.message || "Erro ao carregar DRE.");
    } finally { setLoading(false); }
  }, [request, inicio, fim]);

  useEffect(() => { carregar(); }, [carregar]);

  const casc = dados?.cascata_dre;
  // M-FIN-7 — agregado por centro de custo (o campo que ninguém lia).
  const centros = dados?.por_centro_de_custo || { receitas: [], despesas: [] };
  const temDados = !!dados && Number(dados.lancamentos_count || 0) > 0;

  return (
    <PageContainer style={{ maxWidth: "none" }} className="fin2">
      <div className="flex between" style={{ alignItems: "flex-end", marginBottom: 18, flexWrap: "wrap", gap: 12 }}>
        <div>
          <div className="breadcrumb">
            <span>Início</span><span className="crumb-sep">/</span>
            <span>Financeiro</span><span className="crumb-sep">/</span>
            <span className="active">DRE</span>
          </div>
          <h1 className="page-title">DRE</h1>
          <div className="page-sub">Demonstração de Resultado · método gerencial · regime de competência</div>
        </div>
        <div className="flex" style={{ gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <div className="tabchip-group">
            {PERIODOS.map((p) => (
              <div key={p.id} className={"tabchip" + (periodo === p.id ? " active" : "")}
                   role="button" tabIndex={0} onClick={() => setPeriodo(p.id)}
                   onKeyDown={(e) => e.key === "Enter" && setPeriodo(p.id)}>{p.label}</div>
            ))}
          </div>
          <ExportMenu disabled={!temDados}
                      onExport={(formato) => dados && exportarDre(formato, dados)} />
        </div>
      </div>

      {error && (
        <div className="empty"><div className="msg"><FiAlertTriangle size={14} aria-hidden="true" /> {error}</div></div>
      )}

      <div className="dre">
        <div className="dre__period">
          <h2 className="title">{tituloPeriodo(periodo, hoje)}</h2>
          <span className="range">{brData(inicio)} → {brData(fim)} · período acumulado</span>
        </div>
        <div className="dre-note">
          As 10 classificações DRE espelham o backend (<span className="mono">classificacao_dre</span>).
          Os subtotais (receita líquida, lucro bruto, resultado operacional e líquido) são calculados no
          servidor em regime de competência. Considera apenas lançamentos confirmados.
        </div>

        {!temDados ? (
          <div className="empty"><div className="msg">
            {loading ? "Carregando…" : "Sem lançamentos no período."}
          </div></div>
        ) : (
          <>
            {casc.linhas.map((l) => <DreLinha key={l.chave} l={l} />)}

            {casc.memorando.length > 0 && (
              <>
                <div className={"dre-memo-head" + (memoAberto ? "" : " is-collapsed")}
                     role="button" tabIndex={0} aria-expanded={memoAberto}
                     onClick={() => setMemoAberto((a) => !a)}
                     onKeyDown={(e) => e.key === "Enter" && setMemoAberto((a) => !a)}>
                  <FiChevronDown className="chev" size={15} aria-hidden="true" />
                  Memorando · movimentações que não compõem o resultado
                </div>
                <div className={"dre-memo" + (memoAberto ? "" : " is-collapsed")}>
                  {casc.memorando.map((l) => <DreLinha key={l.chave} l={l} />)}
                </div>
              </>
            )}
          </>
        )}
      </div>

      {/* M-FIN-7: o centro de custo era coletado em todo lançamento,
          gravado, filtrável na API e mostrado na lista — e NENHUM
          relatório agregava por ele. Quem preenchia nunca conseguia
          responder a única pergunta que justifica preencher: "quanto a
          Montagem gastou este mês?". */}
      {centros.despesas.length > 0 && (
        <div className="cc" style={{ marginTop: 14 }}>
          <div className="cc__head">
            <div>
              <div className="cc__title">Por centro de custo</div>
              <div className="cc__sub">no mesmo período · regime de competência</div>
            </div>
          </div>
          <div className="cc__body">
            <table className="dtable">
              <colgroup>
                <col style={{ width: "46%" }} />
                <col className="col-money" />
                <col style={{ width: "12%" }} />
                <col className="col-money" />
              </colgroup>
              <thead>
                <tr>
                  <th>Centro de custo</th>
                  <th className="num" style={{ textAlign: "right" }}>Saídas</th>
                  <th className="num" style={{ textAlign: "right" }}>% das saídas</th>
                  <th className="num" style={{ textAlign: "right" }}>Entradas</th>
                </tr>
              </thead>
              <tbody>
                {centros.despesas.map((d) => {
                  const receita = centros.receitas.find(
                    (r) => r.centro_custo === d.centro_custo,
                  );
                  return (
                    <tr key={d.centro_custo}>
                      <td>{d.centro_custo}</td>
                      <td className="num" style={{ textAlign: "right" }}>
                        {formatarReais(d.total)}
                      </td>
                      <td className="num" style={{ textAlign: "right" }}>{d.pct}%</td>
                      <td className="num" style={{ textAlign: "right" }}>
                        {receita ? formatarReais(receita.total) : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </PageContainer>
  );
}
